import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  CircleAlert,
  MessageSquareText,
  RotateCcw,
  Star,
} from 'lucide-react';
import EmptyState from '../../components/site/EmptyState';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import { productsAPI } from '../../api/apiService';
import { seoConfig } from '../../configs/seoConfig';
import { getAllReviews } from '../../services/reviewService';
import { getErrorMessage } from '../../utils/apiError';
import { formatDate } from '../../utils/format';
import './ReviewsPage.css';

const RATINGS = [5, 4, 3, 2, 1];

const productPath = (id, name) =>
  `/products/${id}/${encodeURIComponent(name || 'project')}`;

// Product names for the "Review of …" links. Reviews still show without them.
const loadProductNames = async () => {
  try {
    const response = await productsAPI.getAll({ itemsPerPage: 100 });
    const products = response.data?.data?.products || [];
    return new Map(products.map(p => [p.id, p.name]));
  } catch {
    return new Map();
  }
};

const Stars = ({ value, size = 'md' }) => {
  const rounded = Math.round(Number(value) || 0);
  return (
    <span
      className={`rv-stars rv-stars--${size}`}
      role="img"
      aria-label={`${Number(value).toFixed(1).replace(/\.0$/, '')} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} aria-hidden="true" className={i <= rounded ? 'is-on' : undefined} />
      ))}
    </span>
  );
};

const Summary = ({ reviews }) => {
  const total = reviews.length;
  const average = reviews.reduce((sum, r) => sum + r.rating, 0) / total;
  const counts = RATINGS.map(rating => ({
    rating,
    count: reviews.filter(r => Math.round(r.rating) === rating).length,
  }));
  const verified = reviews.filter(r => r.isVerifiedPurchase).length;

  return (
    <section className="pk-card rv-summary" aria-label="Rating summary">
      <div className="rv-summary__score">
        <p className="rv-summary__avg">{average.toFixed(1)}</p>
        <Stars value={average} size="lg" />
        <p className="pk-muted rv-summary__meta">
          {total} {total === 1 ? 'review' : 'reviews'}
          {verified > 0 && ` · ${verified} verified ${verified === 1 ? 'purchase' : 'purchases'}`}
        </p>
      </div>
      <ul className="rv-bars">
        {counts.map(({ rating, count }) => (
          <li key={rating} className="rv-bar">
            <span className="rv-bar__label">{rating} star</span>
            <span className="rv-bar__track" aria-hidden="true">
              <span
                className="rv-bar__fill"
                style={{ width: `${total ? (count / total) * 100 : 0}%` }}
              />
            </span>
            <span className="rv-bar__count">
              {count}
              <span className="rv-sr"> {count === 1 ? 'review' : 'reviews'}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const ReviewCard = ({ review, productName }) => (
  <article className="pk-card rv-card">
    <div className="rv-card__top">
      <Stars value={review.rating} />
      <time className="rv-card__date" dateTime={review.createdAt}>
        {formatDate(review.createdAt)}
      </time>
    </div>
    <p className="rv-card__text">{review.comment}</p>
    <footer className="rv-card__foot">
      <div className="rv-card__who">
        <span className="rv-card__name">{review.userName || 'Customer'}</span>
        {review.isVerifiedPurchase && (
          <span className="pk-badge" data-tone="success">
            <BadgeCheck aria-hidden="true" />
            Verified purchase
          </span>
        )}
      </div>
      {productName && (
        <Link
          className="rv-card__product"
          to={productPath(review.productId, productName)}
        >
          {productName}
        </Link>
      )}
    </footer>
  </article>
);

const ReviewsPage = () => {
  const [state, setState] = useState({
    status: 'loading',
    reviews: [],
    names: new Map(),
    error: '',
  });
  const [rating, setRating] = useState(0); // 0 = all

  const load = useCallback(async () => {
    setState(prev => ({ ...prev, status: 'loading', error: '' }));
    try {
      const [response, names] = await Promise.all([
        getAllReviews(),
        loadProductNames(),
      ]);
      const reviews = (Array.isArray(response?.data) ? response.data : [])
        .map(review => ({ ...review, rating: Number(review.rating) || 0 }))
        .filter(review => review.rating > 0)
        .sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        );
      setState({ status: 'ready', reviews, names, error: '' });
    } catch (error) {
      setState({
        status: 'error',
        reviews: [],
        names: new Map(),
        error: getErrorMessage(error, "We couldn't load the reviews."),
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { status, reviews, names } = state;
  const visible = useMemo(
    () =>
      rating ? reviews.filter(r => Math.round(r.rating) === rating) : reviews,
    [reviews, rating]
  );

  let content;
  if (status === 'loading') {
    content = (
      <div className="rv-loading" aria-busy="true" aria-label="Loading reviews">
        <span className="pk-skel rv-loading__summary" />
        <div className="pk-grid pk-grid--2">
          {[0, 1, 2, 3].map(i => (
            <span key={i} className="pk-skel rv-loading__card" />
          ))}
        </div>
      </div>
    );
  } else if (status === 'error') {
    content = (
      <EmptyState
        role="alert"
        icon={<CircleAlert />}
        title="Couldn't load reviews"
        text={state.error}
        actions={
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={load}
          >
            <RotateCcw aria-hidden="true" />
            Retry
          </button>
        }
      />
    );
  } else if (reviews.length === 0) {
    content = (
      <EmptyState
        icon={<MessageSquareText />}
        title="No reviews yet"
        text="Reviews from buyers will appear here."
        actions={
          <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/products">
            Browse projects
          </Link>
        }
      />
    );
  } else {
    content = (
      <>
        <Summary reviews={reviews} />

        <div className="rv-filter" role="group" aria-label="Filter by rating">
          <button
            type="button"
            className="pk-chip"
            aria-pressed={rating === 0}
            onClick={() => setRating(0)}
          >
            All
          </button>
          {RATINGS.map(value => (
            <button
              key={value}
              type="button"
              className="pk-chip"
              aria-pressed={rating === value}
              onClick={() => setRating(rating === value ? 0 : value)}
            >
              {value} star
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <EmptyState
            title={`No ${rating}-star reviews`}
            actions={
              <button
                type="button"
                className="pk-btn pk-btn--secondary pk-btn--sm"
                onClick={() => setRating(0)}
              >
                Show all reviews
              </button>
            }
          />
        ) : (
          <div className="pk-grid pk-grid--2 rv-list">
            {visible.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                productName={names.get(review.productId)}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <main className="pk-page">
      <SEO {...seoConfig.pages.reviews} />
      <div className="pk-container">
        <PageHeader
          title="Customer reviews"
          subtitle="What buyers say about the projects they bought."
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Reviews' }]}
        />

        {content}

        <p className="pk-muted rv-note">
          Bought a project? Review it from your{' '}
          <Link className="pk-link" to="/profile">
            order page
          </Link>
          .
        </p>
      </div>
    </main>
  );
};

export default ReviewsPage;
